import { useState, useCallback, useContext } from 'react';
import { createFolder, renameFolder, deleteFolder, copyFolder, moveFolder, ScrapbookFolder } from '@/api';
import { UserContext } from '@/components';
import { useNotification } from '@/components/general/NotificationProvider';
import FolderTreeView from './FolderTreeView';
import FolderNameDialog from '../dialogs/FolderNameDialog';
import CopyMoveDialog from '../dialogs/CopyMoveDialog';
import DeleteFolderDialog from '../dialogs/DeleteFolderDialog';
import { EditSharing, RemoveSharing } from '@/components/scrapbook';

interface ScrapbookSidebarProps {
  folders: ScrapbookFolder[];
  selectedFolderId: number;
  onFolderSelect: (folder: ScrapbookFolder) => void;
  onRefresh: () => void;
  currentFolder: ScrapbookFolder;
}

export function ScrapbookSidebar({
  folders,
  selectedFolderId,
  onFolderSelect,
  onRefresh,
  currentFolder,
}: ScrapbookSidebarProps) {
  const user = useContext(UserContext);
  const { showNotification } = useNotification();
  const [showDelFolderDialog, setShowDelFolderDialog] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [nameDialogTitle, setNameDialogTitle] = useState('');
  const [nameFor, setNameFor] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [copyMoveMode, setCopyMoveMode] = useState<string | null>(null);
  const [showCopyMoveDialog, setShowCopyMoveDialog] = useState(false);
  const [showEditSharing, setShowEditSharing] = useState(false);
  const [showRemoveSharing, setShowRemoveSharing] = useState(false);

  const rootFolder = folders[0];
  const atRoot = currentFolder?.id === rootFolder?.id;
  const isShared = currentFolder?.isShared || false;
  const hasSharing = currentFolder?.hasSharing || false;

  const findFolderById = (folder: ScrapbookFolder, id: number): ScrapbookFolder | null => {
    if (folder.id === id) return folder;
    if (folder.children) {
      for (const child of folder.children) {
        const found = findFolderById(child, id);
        if (found) return found;
      }
    }
    return null;
  };

  const nameExistsInFolder = (fid: number, name: string): boolean => {
    const actualCurrentFolder = findFolderById(folders[0], fid);
    if (!actualCurrentFolder?.children) return false;
    return actualCurrentFolder.children.some((fldr) => !fldr.isShared && fldr.name === name);
  };

  const handleDragStart = useCallback((event: React.DragEvent, folder: ScrapbookFolder) => {
    localStorage.setItem('drag-obj', JSON.stringify(folder));
    localStorage.setItem('drag-obj-tp', 'folder');
    event.dataTransfer.setData('application/json', JSON.stringify(folder));
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent, targetFolder: ScrapbookFolder) => {
      event.preventDefault();

      const dragType = localStorage.getItem('drag-obj-tp');
      const itemData = JSON.parse(localStorage.getItem('drag-obj') || '{}');

      if (dragType === 'folder' && !itemData.isShared) {
        if (itemData.id === targetFolder.id) {
          console.warn('Cannot move folder into itself.');
          return;
        }

        try {
          const result = await moveFolder(itemData.id, targetFolder.id, user?.userId);
          if (result.succeeded) {
            onRefresh();
          } else {
            showNotification('Move failed: ' + result.message, 'error');
          }
        } catch {
          showNotification('Failed to move folder', 'error');
        }
      }
    },
    [onRefresh, user?.userId, showNotification],
  );

  const newFolderClick = () => {
    setNameFor('new-folder');
    setNameDialogTitle(atRoot ? 'Create new folder' : 'Create new subfolder');
    setShowNameDialog(true);
  };

  const editFolderClick = () => {
    setNameFor('rename-folder');
    setNameDialogTitle(`Rename folder ${currentFolder?.name}`);
    setNewFolderName(currentFolder?.name || '');
    setShowNameDialog(true);
  };

  const handleNameDialogOk = async (name: string) => {
    if (nameFor === 'new-folder') {
      await createNewFolder(name);
    } else if (nameFor === 'rename-folder') {
      await renameCurrentFolder(name);
    }
    setShowNameDialog(false);
    setNameFor(null);
    setNewFolderName('');
    setNameDialogTitle('');
  };

  const handleNameDialogCancel = () => {
    setShowNameDialog(false);
    setNameFor(null);
    setNewFolderName('');
    setNameDialogTitle('');
  };

  const createNewFolder = async (name: string) => {
    const nameExists = nameExistsInFolder(currentFolder.id, name);
    if (nameExists) {
      showNotification(`Can't create folder ${name} as it already exists`, 'error');
      return;
    }

    try {
      const result = await createFolder(currentFolder.id, name);
      if (result.succeeded) {
        showNotification('Create folder succeeded', 'success');
        onRefresh();
      } else {
        showNotification(`Create folder failed: ${result.message}`, 'error');
      }
    } catch {
      showNotification('Server call to create folder failed!', 'error');
    }
  };

  const renameCurrentFolder = async (name: string) => {
    const nameExists = nameExistsInFolder(currentFolder.parentId, name);
    if (nameExists) {
      showNotification(`Can't rename folder to ${name} as it already exists`, 'error');
      return;
    }

    try {
      const result = await renameFolder(currentFolder.id, name);
      if (result.succeeded) {
        onRefresh();
      } else {
        showNotification('Server call to rename folder failed!', 'error');
      }
    } catch {
      showNotification('Server call to rename folder failed!', 'error');
    }
  };

  const deleteFolderClick = () => {
    setShowDelFolderDialog(true);
  };

  const handleDeleteFolder = async () => {
    try {
      const result = await deleteFolder(currentFolder.id);
      if (result.succeeded) {
        onRefresh();
      } else {
        showNotification('Server call to delete folder failed!', 'error');
      }
    } catch {
      showNotification('Server call to delete folder failed!', 'error');
    }
    setShowDelFolderDialog(false);
  };

  const handleDeleteCancel = () => {
    setShowDelFolderDialog(false);
  };

  const copyFolderClick = () => {
    setCopyMoveMode('copy');
    setShowCopyMoveDialog(true);
  };

  const moveFolderClick = () => {
    setCopyMoveMode('move');
    setShowCopyMoveDialog(true);
  };

  const handleCopyMoveOk = async (dstFolder: ScrapbookFolder) => {
    if (!dstFolder || dstFolder.id === currentFolder.id) {
      showNotification('Destination folder is same as source folder', 'error');
      return;
    }

    try {
      const isCopy = copyMoveMode === 'copy';
      const result = isCopy
        ? await copyFolder(currentFolder.id, dstFolder.id, user?.userId)
        : await moveFolder(currentFolder.id, dstFolder.id, user?.userId);

      if (result.succeeded) {
        onRefresh();
      } else {
        showNotification(`${isCopy ? 'Copy' : 'Move'} failed. ${result.message}`, 'warning');
      }
    } catch {
      showNotification(`Unexpected server response`, 'error');
    }

    setCopyMoveMode(null);
    setShowCopyMoveDialog(false);
  };

  const handleCopyMoveCancel = () => {
    setCopyMoveMode(null);
    setShowCopyMoveDialog(false);
  };

  const shareFolderClick = () => {
    setShowEditSharing(true);
  };

  const unshareFolderClick = () => {
    setShowRemoveSharing(true);
  };

  const editFolderSharingClick = () => {
    setShowEditSharing(true);
  };

  return (
    <div className="flex h-full flex-col border-r border-gray-200 bg-white">
      <div className="overflow-y-auto pb-1">
        <FolderTreeView
          folders={folders}
          selectedFolderId={selectedFolderId}
          onFolderSelect={onFolderSelect}
          draggingEnabled={true}
          onDragStart={handleDragStart}
          onDrop={handleDrop}
        />
      </div>
      <div className="space-y-3 border-t border-gray-200 px-3 py-4">
        <button
          onClick={newFolderClick}
          className="w-full cursor-pointer rounded-full border border-black bg-white py-2.5 pr-4 pl-6 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          {atRoot ? 'Create new folder' : 'Create new subfolder'}
        </button>

        <button
          onClick={copyFolderClick}
          disabled={atRoot || isShared}
          className="w-full cursor-pointer rounded-full border border-black bg-white py-2.5 pr-4 pl-6 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
        >
          Copy folder
        </button>

        <button
          onClick={moveFolderClick}
          disabled={atRoot || isShared}
          className="w-full cursor-pointer rounded-full border border-black bg-white py-2.5 pr-4 pl-6 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
        >
          Move folder
        </button>

        {isShared && (
          <button
            onClick={unshareFolderClick}
            className="w-full cursor-pointer rounded-full border border-black bg-white py-2.5 pr-4 pl-6 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Unshare folder
          </button>
        )}

        {!isShared && !hasSharing && (
          <button
            onClick={shareFolderClick}
            disabled={atRoot || isShared}
            className="w-full cursor-pointer rounded-full border border-black bg-white py-2.5 pr-4 pl-6 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
          >
            Share folder
          </button>
        )}

        {hasSharing && (
          <button
            onClick={editFolderSharingClick}
            className="w-full cursor-pointer rounded-full border border-black bg-white py-2.5 pr-4 pl-6 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Edit folder sharing
          </button>
        )}

        <button
          onClick={editFolderClick}
          disabled={atRoot || isShared}
          className="w-full cursor-pointer rounded-full border border-black bg-white py-2.5 pr-4 pl-6 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
        >
          Rename folder
        </button>

        <button
          onClick={deleteFolderClick}
          disabled={atRoot || isShared}
          className="w-full cursor-pointer rounded-full border border-black bg-white py-2.5 pr-4 pl-6 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
        >
          Delete folder
        </button>
      </div>

      <FolderNameDialog
        show={showNameDialog}
        title={nameDialogTitle}
        initialValue={newFolderName}
        onOk={handleNameDialogOk}
        onCancel={handleNameDialogCancel}
      />

      <DeleteFolderDialog
        show={showDelFolderDialog}
        folderName={currentFolder?.name || ''}
        onOk={handleDeleteFolder}
        onCancel={handleDeleteCancel}
      />

      <CopyMoveDialog
        show={showCopyMoveDialog}
        folders={folders}
        onOk={handleCopyMoveOk}
        onCancel={handleCopyMoveCancel}
      />

      <EditSharing
        show={showEditSharing}
        folderId={currentFolder?.id || 0}
        onClose={() => setShowEditSharing(false)}
        onRefresh={onRefresh}
      />

      <RemoveSharing
        show={showRemoveSharing}
        folderId={currentFolder?.id || 0}
        folderName={currentFolder?.name || ''}
        sharer={currentFolder?.sharer || ''}
        onClose={() => setShowRemoveSharing(false)}
        onRefresh={onRefresh}
      />
    </div>
  );
}
